import React from 'react'
import './Style/Components.scss'

interface StepProps {
  index: number
  label: string
  isActive: boolean
  isCompleted: boolean
  onClick?: (step: number) => void
}

interface StepperProps {
  steps: {key?: string; label: string}[]
  activeSteps: Record<string, boolean>
  completedSteps: Record<string, boolean>
  onStepClick?: (step: number) => void
}

const Step: React.FC<StepProps> = ({index, label, isActive, isCompleted, onClick}) => {
  return (
    <div
      className='d-flex flex-column align-items-center position-relative'
      style={{zIndex: 1, width: '120px'}}
    >
      <div
        className={`stepper-circle rounded-circle d-flex justify-content-center align-items-center ${
          isCompleted
            ? 'stepper-circle-completed'
            : isActive
            ? 'stepper-circle-active'
            : 'stepper-circle-inactive'
        }`}
        onClick={() => onClick && onClick(index)}
        style={{cursor: onClick ? 'pointer' : 'default'}}
      >
        {isCompleted ? '' : index + 1}
      </div>
      <div className={`mt-2 text-center ${isActive || isCompleted ? 'stepper-active' : ''}`}>
        {label}
      </div>
    </div>
  )
}

const Stepper: React.FC<StepperProps> = ({steps, activeSteps, completedSteps, onStepClick}) => {
  const getDefaultKeyForIndex = (index: number): string => {
    switch (index) {
      case 0:
        return 'gettingStarted'
      case 1:
        return 'to'
      case 2:
        return 'from'
      case 3:
        return 'message'
      case 4:
        return 'when'
      default:
        return `step${index}`
    }
  }

  const getKeyForStep = (index: number): string => {
    return steps[index]?.key || getDefaultKeyForIndex(index)
  }

  const getActiveStepIndex = (): number => {
    const activeStepKeys = Object.keys(activeSteps)
    const activeStepIndex = activeStepKeys.findIndex((key) => activeSteps[key])
    return activeStepIndex === -1 ? 0 : activeStepIndex
  }

  const getCompletedPercentage = (): number => {
    const activeIndex = getActiveStepIndex()
    return (activeIndex / (steps.length - 1)) * 80
  }

  const activeStepIndex = getActiveStepIndex()

  return (
    <div
      className='d-flex align-items-start justify-content-between position-relative py-4'
      style={{width: '65%'}}
    >
      {/* Connecting lines */}
      <div
        className='position-absolute stepper-line'
        style={{
          top: '33px',
          left: '40px',
          right: '40px',
          zIndex: 0,
        }}
      />

      {/* Active line */}
      <div
        className='position-absolute stepper-line-active'
        style={{
          top: '33px',
          left: '40px',
          width: `${getCompletedPercentage()}%`,
          zIndex: 0,
        }}
      />

      {/* Steps */}
      {steps.map((step, index) => {
        const stepKey = step.key || getDefaultKeyForIndex(index)
        const isCompleted = !!completedSteps[stepKey]
        const isActive = index === activeStepIndex

        return (
          <Step
            key={index}
            index={index}
            label={step.label}
            isActive={isActive}
            isCompleted={isCompleted}
            onClick={onStepClick}
          />
        )
      })}
    </div>
  )
}

export default Stepper
